/**
 * Mapping helpers for native numeric push notification identifiers to API string unions.
 *
 * Native codes (as provided):
 *  Platform: 1 = IOS, 2 = Android
 *  Service:  1 = APNS, 2 = FCM
 *
 * On any unknown code we log an error and throw immediately to avoid silent data corruption.
 */
import { NativePushToken } from "./types";
import { IPushTokenData } from "./pushTokenApi";

export type PushNotificationPlatform = "IOS" | "Android";
export type PushNotificationService = "APNS" | "FCM";

/** Map native platformId (1/2) to API platform string. */
export function mapPlatform(platformId: number): PushNotificationPlatform {
    switch (platformId) {
        case 1:
            return "IOS";
        case 2:
            return "Android";
        default: {
            const message = `Unknown platformId '${platformId}' while mapping push notification platform.`;
            throw new Error(message);
        }
    }
}

/** Map native pushServiceId (1/2) to API service string. */
export function mapService(pushServiceId: number): PushNotificationService {
    switch (pushServiceId) {
        case 1:
            return "FCM"; // Reserved for APNs, which is not implemented
        case 2:
            return "FCM";
        default: {
            const message = `Unknown pushServiceId '${pushServiceId}' while mapping push notification service.`;
            throw new Error(message);
        }
    }
}

/**
 * Build a IPushTokenData object from native push token data.
 * @param native - The native push token data from the mobile app
 * @param userId - The ID of the currently logged-in user
 */
export function buildPushTokenFromNativePushToken(
    native: NativePushToken,
    userId: string
): IPushTokenData {
    return {
        appId: native.appId,
        token: native.token,
        platform: mapPlatform(native.platformId),
        service: mapService(native.pushServiceId),
        user: {
            id: userId,
        },
    };
}
